import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'

import Button from '@/components/Button'
import type { ImageLabel } from '@/utils/constants'

interface AddLabelDialogProps {
  open: boolean
  onClose: () => void
  onSave: (labels: ImageLabel[]) => void
  mode: 'add' | 'edit'
  initialLabels: ImageLabel[]
  allLabels: ImageLabel[]
  onRequestAddCustomLabel: () => void
  onDeleteCustomLabel: (label: ImageLabel) => void
  onEditCustomLabel: (label: ImageLabel) => void
  isSaving?: boolean
  isDeletingLabel?: boolean
}

export default function AddLabelDialog({
  open,
  onClose,
  onSave,
  mode,
  initialLabels,
  allLabels,
  onRequestAddCustomLabel,
  onDeleteCustomLabel,
  onEditCustomLabel,
  isSaving = false,
  isDeletingLabel = false,
}: AddLabelDialogProps) {
  const [selectedLabels, setSelectedLabels] = useState<ImageLabel[]>([])

  useEffect(() => {
    if (open) {
      setSelectedLabels([...initialLabels])
    }
  }, [open])

  const hasChanged = (): boolean => {
    if (selectedLabels.length !== initialLabels.length) return true
    const initialIds = new Set(initialLabels.map((l) => l.id))
    return selectedLabels.some((l) => !initialIds.has(l.id))
  }

  const isSelected = (label: ImageLabel): boolean =>
    selectedLabels.some((l) => l.id === label.id)

  const toggleLabel = (label: ImageLabel) => {
    setSelectedLabels((prev) =>
      isSelected(label)
        ? prev.filter((l) => l.id !== label.id)
        : [...prev, label],
    )
  }

  const removeChip = (label: ImageLabel) => {
    setSelectedLabels((prev) => prev.filter((l) => l.id !== label.id))
  }

  const predefinedLabels = allLabels.filter((l) => l.isPredefined)
  // Custom labels ordered newest → oldest (reversed insertion order)
  const customLabels = allLabels
    .filter((l) => !l.isPredefined)
    .slice()
    .reverse()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: '#292929',
            borderRadius: '8px',
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '5.625rem',
            maxHeight: 'calc(100% - 130px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* ── Title ── */}
      <DialogTitle sx={{ py: 2, px: 3, flexShrink: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography fontWeight={400} fontSize="1.25rem" color="white">
            {mode === 'edit' ? 'Edit Label' : 'Add Label'}
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="close dialog"
            sx={{ p: 0, '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
          >
            <CloseIcon sx={{ color: 'white' }} />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* ── Content: Input + Options ── */}
      <DialogContent
        sx={{
          py: 1.5,
          px: 3,
          paddingTop: '8px !important',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* ── Selected-labels chip input ── */}
        <Box
          sx={{
            border: '1px solid #999',
            borderRadius: '4px',
            minHeight: '48px',
            p: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
            position: 'relative',
            flexShrink: 0,
            mb: 1.5,
          }}
        >
          {/* Floating label */}
          <Typography
            component="span"
            sx={{
              position: 'absolute',
              top: '-9px',
              left: '10px',
              bgcolor: '#292929',
              px: 0.5,
              fontSize: '11px',
              color: '#999',
              lineHeight: 1,
            }}
          >
            Label Name
          </Typography>

          {selectedLabels.length === 0 && (
            <Typography sx={{ color: '#555', fontSize: '13px', pl: 0.5 }}>
              Select
            </Typography>
          )}

          {selectedLabels.map((label) => (
            <Chip
              key={label.id}
              label={label.name}
              size="small"
              onDelete={() => removeChip(label)}
              deleteIcon={
                <CloseIcon
                  sx={{
                    fontSize: '13px !important',
                    color: 'rgba(0,0,0,0.5) !important',
                    '&:hover': { color: 'rgba(0,0,0,0.8) !important' },
                  }}
                />
              }
              sx={{
                bgcolor: '#e0e0e0',
                border: 'none',
                color: '#1a1a1a',
                fontSize: '12px',
                height: '26px',
                fontWeight: 500,
                borderRadius: '6px',
                cursor: 'default',
                '&:hover': { bgcolor: '#e0e0e0' },
                '& .MuiChip-deleteIcon': {
                  margin: '0 4px 0 -2px',
                },
              }}
            />
          ))}
        </Box>

        {/* ── Inline options list ── */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '4px',
            overflow: 'hidden',
          }}
        >
          {/* Scrollable label rows */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: '#ffb572',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-track': { bgcolor: '#1a1a1a' },
            }}
          >
            {/* Predefined labels */}
            {predefinedLabels.map((label) => (
              <LabelListRow
                key={label.id}
                label={label}
                checked={isSelected(label)}
                onToggle={() => toggleLabel(label)}
              />
            ))}

            {/* Custom labels — newest first, with edit + delete icons */}
            {customLabels.map((label) => (
              <LabelListRow
                key={label.id}
                label={label}
                checked={isSelected(label)}
                onToggle={() => toggleLabel(label)}
                onDelete={() => onDeleteCustomLabel(label)}
                onEdit={() => onEditCustomLabel(label)}
                isDeleting={isDeletingLabel}
              />
            ))}
          </Box>

          {/* ── ADD CUSTOM LABEL — sticky at bottom ── */}
          <Box
            sx={{
              bgcolor: '#292929',
              px: 1.5,
              pt: 2,
              pb: 1.5,
              flexShrink: 0,
              borderTop: '1px solid rgba(255, 255, 255, 0.12)',
            }}
          >
            <Box
              id="add-custom-label-btn"
              onClick={onRequestAddCustomLabel}
              display="inline-flex"
              alignItems="center"
              gap={0.5}
              sx={{
                border: '1px solid #90caf9',
                borderRadius: '4px',
                px: 1,
                py: 0.4,
                cursor: 'pointer',
                color: '#90caf9',
                fontSize: '12px',
                fontWeight: 500,
                letterSpacing: '0.4px',
                userSelect: 'none',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: 'rgba(144, 202, 249, 0.08)' },
              }}
            >
              <AddIcon sx={{ fontSize: 14 }} />
              ADD CUSTOM LABEL
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* ── Action buttons ── */}
      <DialogActions sx={{ py: 2, px: 3, flexShrink: 0 }}>
        <Box display="flex" justifyContent="flex-end" gap={2} width="100%">
          <Button
            id="add-label-cancel-btn"
            label="CANCEL"
            variant="outlined"
            textTransform="uppercase"
            onClick={onClose}
          />
          <Button
            id="add-label-save-btn"
            label="SAVE"
            variant="contained"
            color="primary"
            textTransform="uppercase"
            disabled={!hasChanged() || isSaving}
            loading={isSaving}
            onClick={() => onSave(selectedLabels)}
          />
        </Box>
      </DialogActions>
    </Dialog>
  )
}

// ── Label list row ──────────────────────────────────────────────────────────

interface LabelListRowProps {
  label: ImageLabel
  checked: boolean
  onToggle: () => void
  onDelete?: () => void
  onEdit?: () => void
  isDeleting?: boolean
}

function LabelListRow({
  label,
  checked,
  onToggle,
  onDelete,
  onEdit,
  isDeleting = false,
}: LabelListRowProps) {
  const [hovered, setHovered] = useState(false)

  return (
    <Box
      display="flex"
      alignItems="center"
      onClick={onToggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        px: 1.5,
        py: 1,
        cursor: 'pointer',
        bgcolor: checked ? '#3d3d3d' : hovered ? '#333333' : '#292929',
        transition: 'background-color 0.12s ease',
      }}
    >
      <Checkbox
        checked={checked}
        size="small"
        onClick={(e) => e.stopPropagation()}
        onChange={onToggle}
        sx={{
          p: 0,
          mr: 1.5,
          color: '#666',
          '&.Mui-checked': { color: '#90caf9' },
          flexShrink: 0,
        }}
      />
      <Box flex={1} minWidth={0}>
        <Typography
          sx={{
            fontSize: '14px',
            color: 'white',
            fontWeight: checked ? 500 : 400,
            lineHeight: 1.3,
          }}
        >
          {label.name}
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            color: '#888',
            lineHeight: 1.3,
            mt: 0.25,
          }}
        >
          {label.description || '-'}
        </Typography>
      </Box>

      {/* Edit + Delete icons — custom labels only */}
      {onEdit && (
        <IconButton
          size="small"
          aria-label={`Edit custom label ${label.name}`}
          disabled={isDeleting}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onEdit()
          }}
          sx={{
            color: '#90caf9',
            ml: 1,
            p: 0.5,
            flexShrink: 0,
            '&:hover': { bgcolor: 'rgba(144, 202, 249, 0.12)' },
          }}
        >
          <EditIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
      {onDelete && (
        <IconButton
          size="small"
          aria-label={`Delete custom label ${label.name}`}
          disabled={isDeleting}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onDelete()
          }}
          sx={{
            color: '#e46962',
            ml: 0.5,
            p: 0.5,
            flexShrink: 0,
            '&:hover': { bgcolor: 'rgba(228, 105, 98, 0.12)' },
          }}
        >
          <DeleteIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
  )
}
